import { appendAiGrantPreferencesToUrl } from '../aiGrantPreferencesUrl';
import type { AiGrantPreferencesQuery } from '../aiGrantPreferencesUrl';

/** Docker / entrypoint unsubstituted build placeholder (see `.env.production`). */
const VITE_ENV_PLACEHOLDER = /^__VITE_APP_[A-Z0-9_]+__$/;

/**
 * ViewerGrant base URL from env (trimmed).
 * Empty, whitespace-only, missing, or unsubstituted `__VITE_APP_*__` → classic in-app Viewer.
 */
export function getFilegrantViewerBaseUrl(
  env: { VITE_APP_FILEGRANT_VIEWER_URL?: string | null } = import.meta.env,
): string {
  const raw = env.VITE_APP_FILEGRANT_VIEWER_URL;
  if (raw === undefined || raw === null) {
    return '';
  }
  const trimmed = String(raw).trim();
  if (!trimmed || VITE_ENV_PLACEHOLDER.test(trimmed)) {
    return '';
  }
  return trimmed;
}

/**
 * Guest (ViewerGrant iframe) when URL is a real non-empty value.
 * Embed / SharePoint stay classic at call site. BETA flag is not required — URL alone selects mode.
 */
export function isFilegrantViewerEnabled(
  env: { VITE_APP_FILEGRANT_VIEWER_URL?: string | null } = import.meta.env,
): boolean {
  return Boolean(getFilegrantViewerBaseUrl(env));
}

/**
 * Handoff URL for ViewerGrant `/open` entry:
 * `${VITE_APP_FILEGRANT_VIEWER_URL}/open?fileId=…&refreshToken=…` + prefs (+ optional tokens).
 */
export function buildFilegrantViewerOpenUrl(params: {
  viewerBaseUrl: string;
  fileId: string | number;
  refreshToken?: string | null;
  isReadOnly?: boolean;
  preferences: Omit<AiGrantPreferencesQuery, 'refreshToken'>;
  version?: string | null;
  publicFileToken?: string | null;
  acToken?: string | null;
}): string {
  const base = params.viewerBaseUrl.replace(/\/$/, '');
  const openUrl = new URL(`${base}/open`);
  openUrl.searchParams.set('fileId', String(params.fileId));
  if (params.isReadOnly) {
    openUrl.searchParams.set('r', '1');
  }
  if (params.version) {
    openUrl.searchParams.set('version', params.version);
  }
  if (params.publicFileToken) {
    openUrl.searchParams.set('publicFileToken', params.publicFileToken);
  }
  if (params.acToken) {
    openUrl.searchParams.set('acToken', params.acToken);
  }
  return appendAiGrantPreferencesToUrl(openUrl.toString(), {
    ...params.preferences,
    refreshToken: params.refreshToken,
  });
}
